import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import Select from "react-select";
import moment from "moment";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import CanceladoSefazBadge, { isCanceladoSefaz } from "../../../components/cte/CanceladoSefazBadge";
import ExportarPesquisaButton from "../../../components/ExportarPesquisaButton";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { reactSelectPortalProps, reactSelectStyles } from "../../../utils/reactSelectStyles";
import { filtrosDaUrl, urlComFiltros } from "../../../utils/filtrosUrl";
import { atalhosDePeriodo, descricaoPeriodo, validarPeriodo } from "../../../utils/periodoPesquisa";
import { formatDocument } from "../../../utils/formatDoc";
import PesquisaCteDetailModal from "./PesquisaCteDetailModal";
import { TipoBadge, TomadorCell } from "./componentes";
import {
  CHAVES_AVANCADAS,
  DEFINICAO_FILTROS,
  PAGE_SIZES,
  SITUACAO_OPTIONS,
  TETO_DIAS,
  TIPO_DATA_OPTIONS,
  TIPO_OPTIONS,
  buscaDireta,
  comChaveParcial,
  paramsDaApi,
} from "./filtros";

import "../ConsultaNotasFiscais/styles.css";
import "./styles.css";

// Consulta CT-e (PO1-2968, épico PO1-2280; chamada Pesquisa CT-e até 31/10/2026): consulta leve, somente leitura, no
// molde da Consulta NFe. Diferenças de propósito: ordenação no servidor,
// filtros na URL (compartilháveis), seletor de data Emissão/Importação, colunas
// de tomador e valor da mercadoria e exportação em segundo plano. Também é a
// consulta de CT-e do Smartdash (PO1-3464). Nenhuma ação de OK/NOK/conciliação.

const emptyResult = {
  items: [],
  resumo: { total: 0, valorFrete: 0, valorMercadoria: 0 },
  paginacao: { pagina: 1, tamanhoPagina: 25, total: 0, totalPaginas: 0 },
};

const fmtNumber = (value) => new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(value || 0));
const fmtCurrency = (value) =>
  value == null ? "-" : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
const fmtDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "-");
const fmtDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

const TABLE_COLUMNS = [
  { key: "numero", label: "CT-e", sortField: "numero" },
  { key: "tipo", label: "Tipo" },
  { key: "emissao", label: "Emissão", sortField: "emissao" },
  { key: "importacao", label: "Importação", sortField: "importacao" },
  { key: "transportadora", label: "Transportadora", sortField: "transportadora" },
  { key: "remetente", label: "Remetente" },
  { key: "destinatario", label: "Destinatário" },
  { key: "tomador", label: "Tomador" },
  { key: "valorMercadoria", label: "Valor mercadoria", sortField: "valorMercadoria", numeric: true },
  { key: "valorFrete", label: "Valor frete", sortField: "valorFrete", numeric: true },
  { key: "quantidadeNotas", label: "NF", numeric: true },
  { key: "situacao", label: "Situação" },
];

const fieldInputStyle = {
  background: AUTOPILOT_COLORS.inputBg,
  borderColor: AUTOPILOT_COLORS.inputBorder,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

const quickButtonStyle = (active) => ({
  borderRadius: 999,
  border: `1px solid ${active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
  background: active ? "var(--theme-accent-glow)" : AUTOPILOT_COLORS.inputBg,
  color: active ? AUTOPILOT_COLORS.title : AUTOPILOT_COLORS.text,
  fontSize: "0.78rem",
  fontWeight: 600,
  padding: "0.38rem 0.75rem",
  cursor: "pointer",
  boxShadow: active ? "var(--everlog-card-interactive-shadow)" : "none",
});

const selectValue = (options, value) => options.find((option) => String(option.value) === String(value == null ? "" : value)) || options[0];

const ATALHOS = atalhosDePeriodo(TETO_DIAS);

const mensagemErro = (requestError, padrao) => {
  const erro = requestError && requestError.response && requestError.response.data && requestError.response.data.error;
  if (typeof erro === "string") return erro;
  if (erro && typeof erro === "object") return Object.values(erro).join(" ");
  return padrao;
};

const PesquisaCte = () => {
  const { empresaId, transportadoraId, optionsTransportadora } = useCompanies();
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState(() => filtrosDaUrl(location.search, DEFINICAO_FILTROS));
  const [draftFilters, setDraftFilters] = useState(() => filtrosDaUrl(location.search, DEFINICAO_FILTROS));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [result, setResult] = useState(emptyResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [periodoErro, setPeriodoErro] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  // Filtros ⇄ URL (replace, sem empilhar histórico; padrões saem da URL).
  useEffect(() => {
    const atual = location.search || "";
    const nova = urlComFiltros(atual, filters, DEFINICAO_FILTROS);
    if (nova !== atual) history.replace({ pathname: location.pathname, search: nova });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const params = useMemo(() => paramsDaApi(filters, { embarcadorId: empresaId, transportadoraId }), [empresaId, filters, transportadoraId]);
  const paramsExportacao = useMemo(
    () => paramsDaApi(filters, { embarcadorId: empresaId, transportadoraId }, { paraExportar: true }),
    [empresaId, filters, transportadoraId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/pesquisa/ctes", { params });
      setResult(response.data || emptyResult);
    } catch (requestError) {
      setResult(emptyResult);
      setError(mensagemErro(requestError, "Não foi possível pesquisar os CT-e."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  // Troca de empresa/transportadora no cabeçalho volta para a página 1 (não na
  // montagem: a página pode ter vindo na URL compartilhada).
  const montouRef = useRef(false);
  useEffect(() => {
    if (!montouRef.current) {
      montouRef.current = true;
      return;
    }
    setFilters((current) => (current.pagina === 1 ? current : { ...current, pagina: 1 }));
  }, [empresaId, transportadoraId]);

  const commitFilters = useCallback(
    (next, { manterPagina = false } = {}) => {
      const erro = validarPeriodo(
        { dataInicial: next.dataInicial, dataFinal: next.dataFinal, buscaDireta: buscaDireta(next), comChaveParcial: comChaveParcial(next) },
        TETO_DIAS
      );
      setPeriodoErro(erro || "");
      if (erro) {
        addToast({ place: "tr", type: "warning", title: "Período inválido", description: erro, icon: "tim-icons icon-alert-circle-exc" });
        return false;
      }
      const safe = manterPagina ? { ...next } : { ...next, pagina: 1 };
      setDraftFilters(safe);
      setFilters(safe);
      return true;
    },
    [addToast]
  );

  const updateDraft = (field, value) => setDraftFilters((current) => ({ ...current, [field]: value }));
  const handleApply = () => commitFilters({ ...draftFilters });
  const resetFilters = () => {
    setPeriodoErro("");
    const limpos = filtrosDaUrl("", DEFINICAO_FILTROS);
    setDraftFilters(limpos);
    setFilters(limpos);
  };
  const applyShortcut = (shortcut) => commitFilters({ ...draftFilters, ...shortcut.getDates() });

  const handleSort = (field) => {
    const dir = filters.ordenarPor === field && filters.ordem === "desc" ? "asc" : filters.ordenarPor === field ? "desc" : "desc";
    commitFilters({ ...filters, ordenarPor: field, ordem: dir });
  };
  const handlePageChange = (pagina) => commitFilters({ ...filters, pagina }, { manterPagina: true });
  const handlePageSizeChange = (tamanhoPagina) => commitFilters({ ...filters, tamanhoPagina });

  const toggleCancelados = () => commitFilters({ ...draftFilters, situacao: filters.situacao === "cancelado" ? "" : "cancelado" });

  const activeFiltersCount = useMemo(() => {
    const avancados = CHAVES_AVANCADAS.filter((key) => filters[key] !== null && filters[key] !== undefined && filters[key] !== "").length;
    const periodo = filters.dataInicial || filters.dataFinal || filters.tipoData !== "emissao" ? 1 : 0;
    return avancados + periodo;
  }, [filters]);

  const activeShortcut = useMemo(() => {
    const match = ATALHOS.find((shortcut) => {
      const dates = shortcut.getDates();
      return dates.dataInicial === draftFilters.dataInicial && dates.dataFinal === draftFilters.dataFinal;
    });
    return match ? match.label : null;
  }, [draftFilters.dataInicial, draftFilters.dataFinal]);

  const summary = result.resumo || emptyResult.resumo;
  const pagination = result.paginacao || emptyResult.paginacao;
  const items = result.items || [];
  const sort = { field: filters.ordenarPor, dir: filters.ordem };
  const canceladosAtivo = filters.situacao === "cancelado";

  const transportadoraOptions = useMemo(
    () => [{ value: "", label: "Todas" }].concat((optionsTransportadora || []).map((t) => ({ value: String(t.value), label: t.label }))),
    [optionsTransportadora]
  );

  return (
    <div className="content nfe-consulta-page pesquisa-cte-page">
      <PageHero
        eyebrow="Consultas"
        title="Consulta CTE"
        description="Encontre um CT-e por chave, número ou período e consulte identificação, partes, valores e notas vinculadas, sem passar pela auditoria."
        actions={
          <>
            <PageHeroRefreshButton onClick={load} loading={loading} />
            <ExportarPesquisaButton tipo="cte" filtros={paramsExportacao} disabled={loading || !pagination.total} />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Chave de acesso"
        searchHint={descricaoPeriodo({ ...filters, buscaDireta: buscaDireta(filters) })}
        searchPlaceholder="Chave de acesso do CT-e (44 dígitos ou parte)"
        searchValue={draftFilters.chave}
        onSearchChange={(value) => updateDraft("chave", value)}
        onSearchSubmit={(value) => commitFilters({ ...draftFilters, chave: value })}
        activeFiltersCount={activeFiltersCount}
        open={advancedOpen}
        onToggle={setAdvancedOpen}
        panelTitle="Filtros"
        panelDescription={`Período por emissão ou importação (até ${TETO_DIAS} dias; com a chave completa ou o número o período é ignorado; com parte da chave, até 31 dias), identificação do CT-e, tipo, situação, NF vinculada e tomador.`}
        onApply={handleApply}
        onClear={resetFilters}
        busy={loading}
      >
        <Row>
          <Col md="2">
            <FormGroup>
              <Label>Data</Label>
              <Select
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                isSearchable={false}
                options={TIPO_DATA_OPTIONS}
                value={selectValue(TIPO_DATA_OPTIONS, draftFilters.tipoData)}
                onChange={(option) => updateDraft("tipoData", option ? option.value : "emissao")}
                aria-label="Tipo de data"
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pcte-data-inicial">Data inicial</Label>
              <Input id="pcte-data-inicial" type="date" value={draftFilters.dataInicial} onChange={(e) => updateDraft("dataInicial", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pcte-data-final">Data final</Label>
              <Input id="pcte-data-final" type="date" value={draftFilters.dataFinal} onChange={(e) => updateDraft("dataFinal", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Atalhos</Label>
              <div className="nfe-consulta-shortcuts">
                {ATALHOS.map((shortcut) => (
                  <button key={shortcut.label} type="button" style={quickButtonStyle(activeShortcut === shortcut.label)} onClick={() => applyShortcut(shortcut)} disabled={loading}>
                    {shortcut.label}
                  </button>
                ))}
              </div>
            </FormGroup>
          </Col>
        </Row>
        {periodoErro ? <div className="pesquisa-doc-periodo-erro" data-testid="pcte-periodo-erro">{periodoErro}</div> : null}

        <Row>
          <Col md="2">
            <FormGroup>
              <Label for="pcte-numero">Número</Label>
              <Input id="pcte-numero" value={draftFilters.numero} onChange={(e) => updateDraft("numero", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="1">
            <FormGroup>
              <Label for="pcte-serie">Série</Label>
              <Input id="pcte-serie" value={draftFilters.serie} onChange={(e) => updateDraft("serie", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label>Transportadora</Label>
              <Select
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                options={transportadoraOptions}
                isDisabled={Boolean(transportadoraId)}
                value={selectValue(transportadoraOptions, draftFilters.transportadoraId)}
                onChange={(option) => updateDraft("transportadoraId", option && option.value ? Number(option.value) : null)}
                aria-label="Transportadora"
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Tipo</Label>
              <Select
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                isSearchable={false}
                options={TIPO_OPTIONS}
                value={selectValue(TIPO_OPTIONS, draftFilters.tipo)}
                onChange={(option) => updateDraft("tipo", option ? option.value : "")}
                aria-label="Tipo de CT-e"
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Situação</Label>
              <Select
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                isSearchable={false}
                options={SITUACAO_OPTIONS}
                value={selectValue(SITUACAO_OPTIONS, draftFilters.situacao)}
                onChange={(option) => updateDraft("situacao", option ? option.value : "")}
                aria-label="Situação"
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pcte-cnpj-tomador">CNPJ do tomador</Label>
              <Input id="pcte-cnpj-tomador" value={draftFilters.cnpjTomador} onChange={(e) => updateDraft("cnpjTomador", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="4">
            <FormGroup>
              <Label for="pcte-chave-nf">Chave da NF vinculada</Label>
              <Input id="pcte-chave-nf" value={draftFilters.chaveNf} onChange={(e) => updateDraft("chaveNf", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pcte-numero-nf">Número da NF</Label>
              <Input id="pcte-numero-nf" value={draftFilters.numeroNf} onChange={(e) => updateDraft("numeroNf", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup title="Resumo do recorte" description="Totais da consulta aplicada. Clique em Cancelados para filtrar; clique de novo para desfazer.">
        <MetricCard label="CT-e no recorte" value={fmtNumber(summary.total)} helper={`${fmtNumber(pagination.totalPaginas)} página(s)`} iconClassName="tim-icons icon-paper" tone="info" />
        <MetricCard label="Valor do frete" value={fmtCurrency(summary.valorFrete)} helper="soma de Vl. frete a receber" iconClassName="tim-icons icon-money-coins" tone="success" />
        <MetricCard label="Valor da mercadoria" value={fmtCurrency(summary.valorMercadoria)} helper="soma do valor da carga" iconClassName="tim-icons icon-bag-16" tone="neutral" />
        <MetricCard
          label="Cancelados na SEFAZ"
          value={canceladosAtivo ? fmtNumber(summary.total) : "filtrar"}
          helper={canceladosAtivo ? "mostrando só cancelados" : "clique para ver só cancelados"}
          iconClassName="tim-icons icon-simple-remove"
          tone="danger"
          active={canceladosAtivo}
          onClick={toggleCancelados}
        />
      </MetricCardGroup>

      <ShellCard className="nfe-consulta-table-card" title="CT-e encontrados" description={`${fmtNumber(pagination.total)} resultado(s)`}>
        <TableSurface>
          {error ? <div className="nfe-consulta-state nfe-consulta-state--error" data-testid="pcte-erro">{String(error)}</div> : null}
          {!error && loading ? (
            <div className="nfe-consulta-state">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Pesquisando CT-e...
            </div>
          ) : null}
          {!error && !loading && items.length === 0 ? <div className="nfe-consulta-state">Nenhum CT-e encontrado para os filtros informados.</div> : null}

          {!error && !loading && items.length > 0 ? (
            <Table className="nfe-consulta-table pesquisa-cte-table hub-thead-accent" size="sm" responsive hover data-testid="pcte-tabela">
              <thead className="text-primary">
                <tr>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} className={col.numeric ? "is-number" : undefined}>
                      {col.sortField ? <SortHeader label={col.label} field={col.sortField} sort={sort} onSort={handleSort} align={col.numeric ? "right" : "left"} /> : col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} data-testid="pcte-linha">
                    <td>
                      <button type="button" className="nfe-consulta-document-link" onClick={() => setSelectedId(item.id)}>
                        {item.numero} / {item.serie}
                      </button>
                      <small className="nfe-consulta-chave" title={item.chave || undefined}>
                        <span className="nfe-consulta-chave__valor">{item.chave || "Sem chave"}</span>
                      </small>
                    </td>
                    <td><TipoBadge item={item} /></td>
                    <td className="nfe-consulta-nowrap">{fmtDate(item.emissao)}</td>
                    <td className="nfe-consulta-nowrap">{fmtDateTime(item.importacao)}</td>
                    <td><strong>{item.transportadora || "-"}</strong></td>
                    <td>{item.remetente || "-"}</td>
                    <td>
                      {item.destinatario || "-"}
                      {item.destinatarioCnpj ? <span className="pesquisa-doc-sub">{formatDocument(item.destinatarioCnpj)}</span> : null}
                    </td>
                    <td><TomadorCell item={item} /></td>
                    <td className="is-number">{fmtCurrency(item.valorMercadoria)}</td>
                    <td className="is-number">{fmtCurrency(item.valorFreteReceber)}</td>
                    <td className="is-number">{fmtNumber(item.quantidadeNotas)}</td>
                    <td>
                      {isCanceladoSefaz(item) ? <CanceladoSefazBadge compact /> : <span className="nfe-consulta-status nfe-consulta-status--neutral">{item.situacaoFatura || "Sem fatura"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}

          <TablePagination
            page={pagination.pagina || filters.pagina}
            pageSize={filters.tamanhoPagina}
            total={pagination.total || 0}
            pageSizes={PAGE_SIZES}
            itemLabel="CT-e"
            disabled={loading}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </TableSurface>
      </ShellCard>

      <PesquisaCteDetailModal isOpen={Boolean(selectedId)} cteId={selectedId} embarcadorId={empresaId} transportadoraId={transportadoraId} onClose={() => setSelectedId(null)} />
    </div>
  );
};

export default PesquisaCte;
